// Utilities for handling of LedRule.Keys description as a set of key positions.
import { assertUnreachable, PairSet } from "@/utils";
import { Keys } from "@/keyboard";
import { isKeysAll, isKeysRows, isKeysCols, isKeysKeys } from "@/keyboard/leds";
import { useLayoutStore } from "@/stores";

export const toSelectedKeys = (keys: Keys | null): PairSet => {
  const layout = useLayoutStore();
  const set = new PairSet();
  layout.layout
    .iter()
    .filter((k) => {
      if (isKeysAll(keys)) return true;
      else if (isKeysRows(keys)) return keys.Rows.includes(k.row);
      else if (isKeysCols(keys)) return keys.Cols.includes(k.col);
      else if (isKeysKeys(keys))
        return keys.Keys.find(([r, c]) => r == k.row && c == k.col) != null;
      else return assertUnreachable(keys);
    })
    .forEach((k) => set.add(k.row, k.col));
  return set;
};

type Counts = Record<number, number>;

export const fromSelectedKeys = (selected: PairSet): Keys | null => {
  const layout = useLayoutStore();
  if (selected.count == layout.allCount) return null;

  // Determine if we can optimize to Rows/Cols instead of Keys.
  const keys = selected.iter();
  const row_counts: Counts = {};
  const col_counts: Counts = {};
  for (const [row, col] of keys) {
    row_counts[row] = (row_counts[row] ?? 0) + 1;
    col_counts[col] = (col_counts[col] ?? 0) + 1;
  }

  const allFull = (counts: Counts, fullCounts: Counts): boolean =>
    Object.entries(counts).every(
      ([row_or_col, count]) => fullCounts[Number(row_or_col)] == count
    );
  if (allFull(row_counts, layout.rowCounts))
    return { Rows: Object.keys(row_counts).map(Number) };
  if (allFull(col_counts, layout.columnCounts))
    return { Cols: Object.keys(col_counts).map(Number) };

  return { Keys: keys };
};

export const keysDescription = (keys: Keys | null) => {
  if (isKeysAll(keys)) return "All";
  // else if (isKeysRows(keys)) return "Rows: " + keys.Rows.join(", ");
  else if (isKeysRows(keys)) return `#${keys.Rows.length} rows`;
  else if (isKeysCols(keys))
    // return "Columns: " + keys.Cols.join(", ");
    return `#${keys.Cols.length} columns`;
  else if (isKeysKeys(keys)) return `#${keys.Keys.length}`;
  else return assertUnreachable(keys);
};
